import React from 'react';
import { Alert, Button, Tag, Typography } from 'antd';
import { ArrowRightOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { DailyTradingAdviceResponse } from '@/api/weatherTradingAdvice';

const { Text } = Typography;

function fmtMw(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return `${Math.round(value).toLocaleString('zh-CN')} MW`;
}

function fmtPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return `${Math.round(value * 100)}%`;
}

const riskColor: Record<string, string> = {
  high: 'red',
  medium: 'gold',
  low: 'green',
};

const actionColor: Record<string, string> = {
  conservative: 'red',
  normal: 'green',
  aggressive: 'orange',
  wait: 'default',
};

const sourceStatusMeta: Record<string, { label: string; color: string }> = {
  ok: { label: '正常', color: 'green' },
  stale: { label: '过期', color: 'orange' },
  missing: { label: '缺失', color: 'red' },
  partial: { label: '部分', color: 'gold' },
};

interface Props {
  data: DailyTradingAdviceResponse;
  onOpenEvidence: () => void;
}

const WeatherTradingAdviceHero: React.FC<Props> = ({ data, onOpenEvidence }) => {
  const { advice, solar } = data;

  return (
    <section style={{ border: '1px solid #d9e2ec', borderRadius: 8, background: '#fff', padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <ExclamationCircleOutlined style={{ color: advice.trading_action === 'conservative' ? '#a8071a' : '#237804', fontSize: 18 }} />
        <Tag color={actionColor[advice.trading_action] ?? 'default'}>{advice.trading_action_label}</Tag>
        <Tag color={riskColor[advice.risk_level] ?? 'default'}>风险 {advice.risk_level === 'high' ? '高' : advice.risk_level === 'medium' ? '中' : '低'}</Tag>
        <Tag>可信度 {advice.confidence_label}</Tag>
        <Text type="secondary">D+1 {data.date ?? '-'}</Text>
      </div>

      {data.source_freshness?.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>数据源：</Text>
          {data.source_freshness.map((item) => {
            const meta = sourceStatusMeta[item.status] ?? { label: item.status, color: 'default' };
            return (
              <Tag
                key={item.source}
                color={meta.color}
                title={item.message || item.blocking_reason || undefined}
                style={{ marginInlineEnd: 0 }}
              >
                {item.label} {meta.label}
              </Tag>
            );
          })}
        </div>
      )}

      <div style={{ fontSize: 20, lineHeight: 1.5, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
        {advice.one_sentence}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
        <Metric label="官方集中式预测" value={fmtMw(solar.official_midday_mw)} />
        <Metric label="建议兑现折扣" value={fmtPct(solar.recommended_discount_ratio)} danger={advice.trading_action === 'conservative'} />
        <Metric label="修正后可兑现" value={fmtMw(solar.recommended_realizable_mw)} />
        <Metric label="预计缺口" value={fmtMw(solar.expected_shortfall_mw)} danger={(solar.expected_shortfall_mw ?? 0) >= 5000} />
      </div>

      {advice.reason_cards?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>关键理由</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8 }}>
            {advice.reason_cards.slice(0, 4).map((card) => (
              <div key={card.title} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <Text strong style={{ fontSize: 13 }}>{card.title}</Text>
                  <Tag color={card.severity === 'high' ? 'red' : card.severity === 'medium' ? 'gold' : 'green'} style={{ marginInlineEnd: 0 }}>
                    {card.severity === 'high' ? '高' : card.severity === 'medium' ? '中' : '低'}
                  </Tag>
                </div>
                <Text style={{ fontSize: 12, color: '#4b5563' }}>{card.summary}</Text>
              </div>
            ))}
          </div>
        </div>
      )}

      <Alert
        type={advice.trading_action === 'conservative' ? 'warning' : 'info'}
        showIcon
        message="申报动作"
        description={(
          <div style={{ display: 'grid', gap: 4 }}>
            <div><Text strong>11:00：</Text>{advice.declaration_guidance.at_1100}</div>
            <div><Text strong>14:30：</Text>{advice.declaration_guidance.at_1430}</div>
          </div>
        )}
        style={{ marginBottom: 10 }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Text type="secondary">主要区域：</Text>
        {advice.main_regions.length > 0 ? advice.main_regions.map((region) => <Tag key={region}>{region}</Tag>) : <Tag>全省</Tag>}
        <div style={{ flex: 1 }} />
        <Button size="small" icon={<ArrowRightOutlined />} onClick={onOpenEvidence}>
          查看佐证
        </Button>
      </div>
    </section>
  );
};

const Metric: React.FC<{ label: string; value: string; danger?: boolean }> = ({ label, value, danger }) => (
  <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', background: '#fafafa', minWidth: 0 }}>
    <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
    <div style={{ fontSize: 20, fontWeight: 700, color: danger ? '#a8071a' : '#1f2937', overflowWrap: 'anywhere' }}>{value}</div>
  </div>
);

export default WeatherTradingAdviceHero;
